/**
 * Admin Route Guard Component
 * 
 * Protects admin routes - only allows access to the platform owner.
 * Non-owners are redirected to /feed with an error toast.
 * 
 * SECURITY: Owner identity is hardcoded and cannot be changed without code deployment.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AdminRouteGuard({ children }) {
  const { isAuthenticated, isPlatformOwner, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      toast.error('Please sign in to access this page');
      navigate('/login');
      return;
    }

    if (!isPlatformOwner) {
      toast.error('Not authorized');
      navigate('/feed');
      return;
    }
  }, [isAuthenticated, isPlatformOwner, loading, navigate]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show loading if not authorized (will redirect)
  if (!isAuthenticated || !isPlatformOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Render children if authorized
  return children;
}

/**
 * Owner Controls Banner
 * 
 * Shows a private indicator in admin pages that only the owner can see.
 * This is NOT shown publicly.
 */
export function OwnerControlsBanner() {
  const { isPlatformOwner } = useAuth();

  if (!isPlatformOwner) return null;

  return (
    <div 
      className="bg-purple-500/10 border border-purple-500/30 text-purple-300 px-4 py-2 text-sm flex items-center gap-2 rounded-lg mb-4"
      data-testid="owner-controls-banner"
    >
      <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
      Owner Controls Enabled
    </div>
  );
}
